import { useEffect, useId, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

import { schedulingRules, type RemovalReason, type RemovalRule } from '../schedulingRules'
import type { Job, JobWorker, Worker } from '../types'

const COMMITTED_TEXT: Partial<Record<JobWorker['status'], string>> = {
  on_the_way: 'is already on the way',
  checked_in: 'has already checked in',
  started: 'has already started the job',
}

export interface RemoveWorkerRequest {
  job: Job
  worker: Worker | null
  jobWorker: JobWorker
  rule: RemovalRule
}

/**
 * Confirms removing a worker. Committed workers (accepted / on the way / started) get a
 * stronger warning and a mandatory reason; the note is optional.
 */
export function RemoveWorkerDialog({
  request,
  onCancel,
  onConfirm,
}: {
  request: RemoveWorkerRequest | null
  onCancel: () => void
  onConfirm: (details: { reason: RemovalReason | null; note: string | null }) => void
}) {
  const [reason, setReason] = useState<RemovalReason | ''>('')
  const [note, setNote] = useState('')
  const reasonId = useId()
  const noteId = useId()

  useEffect(() => {
    setReason('')
    setNote('')
  }, [request?.job.id, request?.jobWorker.workerId])

  const name = request?.worker?.name ?? 'this worker'
  const requiresReason = Boolean(request?.rule.requiresReason)
  const committed = request
    ? COMMITTED_TEXT[request.jobWorker.status] ?? (request.jobWorker.response === 'accepted' ? 'has already accepted' : null)
    : null
  const canConfirm = !requiresReason || reason !== ''

  return (
    <AlertDialog open={Boolean(request)} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {name} from job {request?.job.bookingNo}?</AlertDialogTitle>
          <AlertDialogDescription>The worker will be notified.</AlertDialogDescription>
        </AlertDialogHeader>

        {requiresReason ? (
          <div className="space-y-3">
            <p className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {name} {committed ?? 'is committed to this job'}. Removing them now may leave {request?.job.bookingNo} short-staffed
                {request?.job.status === 'in_progress' ? ' while it is in progress' : ''}.
              </span>
            </p>
            <div className="space-y-1.5">
              <Label htmlFor={reasonId}>Reason <span className="text-red-600">*</span></Label>
              <Select value={reason} onValueChange={(value) => setReason(value as RemovalReason)}>
                <SelectTrigger id={reasonId} aria-required="true" className="w-full">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {schedulingRules.removalReasons.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={noteId}>Note (optional)</Label>
              <Textarea id={noteId} value={note} onChange={(event) => setNote(event.target.value)} rows={2} placeholder="Anything the worker or team should know" />
            </div>
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={!canConfirm}
            onClick={() => onConfirm({ reason: reason || null, note: note.trim() || null })}
          >
            Remove worker
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
